import { ref } from 'vue'
import { Capacitor } from '@capacitor/core';
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings';
import { Geolocation } from '@capacitor/geolocation';
import { Diagnostic } from '@awesome-cordova-plugins/diagnostic';
import axios from 'axios';
import store from '../store/index.js';
import settings from '../WiseSettings.js';

const locations = ref([]);
let warmUpInProgress = false;

const loadAccessControlData = async () => {
    const { ajaxUrl, appauth } = store.getters['common/activeHost']
    
    try {
        const { data } = await axios.get(`${ ajaxUrl }?controller=ajax&getaccesscontrolappdata=1&appauth=${ appauth }`)

        console.log(data);
        if (Array.isArray(data.locations)) {
            locations.value = data.locations
        } else {
            locations.value = []
        }
    } catch (e) {
        console.log('loadAccessControlData', e)
    }
    
}

export default function useLocation() {
    const device = Capacitor.getPlatform();
    const { maps } = window.google;
    const getCurrentPosition = async (raw = false) => {
        const position = await Geolocation.getCurrentPosition({ enableHighAccuracy: true })

        if (!position || !position.coords) {
            console.warn('latLng problem', { position })
            return null;
        }

        if (raw) return position;

        const { latitude, longitude } = position.coords

        if (!latitude || !longitude) {
            console.warn('latLng problem', { latitude, longitude })
            return null;
        }

        return new maps.LatLng(latitude, longitude)
    }

    return {
        locations,
        loadAccessControlData,
        getCurrentPosition,
        async handlePermission() {
            if (device === 'web') {
                return false;
            }

            try {
                const { location } = await Geolocation.checkPermissions()

                console.log('checkPermissions', { location })

                if (location === 'denied') {
                    return false;
                } else if (location === 'granted') {
                    return true;
                }
            } catch(e) {
                console.log('checkPermissions error', e)
                return false;
            }
            
            try {
                const { location /*, coarseLocation*/ } = await Geolocation.requestPermissions(['location'])

                console.log('requestPermissions', { location })

                return location === 'granted';
            } catch(e) {
                console.log('requestPermissions error', e)
                return false;
            }
        },
        /**
         * 
         * @param {Array} locationArea array of lat, lng objects
         * @returns {Boolean}
         */
        async checkAreaLocation(locationArea) {
            const position = await getCurrentPosition()

            if (!position) return false;
            
			const areaPolygon = new maps.Polygon({
				paths: locationArea.map(latLng => new maps.LatLng(latLng.lat, latLng.lng))
			})

            return maps.geometry.poly.containsLocation(position, areaPolygon)
        },
        /**
         * 
         * @param {Object} coordinate 
         * @param {Number} coordinate.lat 
         * @param {Number} coordinate.lng 
         * @param {Number} radius radius in meters
         * @returns {Boolean}
         */
        async checkRadiusLocation(coordinate, radius) {
            const position = await getCurrentPosition()

            if (!position) return false;
            
            const distance = maps.geometry.spherical.computeDistanceBetween(
                position,
                new maps.LatLng(coordinate.lat, coordinate.lng)
            )
            console.log('checkRadiusLocation', distance, radius)
            return distance <= radius
        },
        locationServicesAvailable() {
            const device = Capacitor.getPlatform()

            return ['ios', 'android'].includes(device)
        },
        openSettings() {
            const device = Capacitor.getPlatform()

			if (device === 'ios') {
				NativeSettings.openIOS({
					option: IOSSettings.App
				})
			} else if (device === 'android') {
				NativeSettings.openAndroid({
					option: AndroidSettings.ApplicationDetails
				})
			} else {
				console.log("Setting open fired but platform is not iOS or Android")
			}
        },
        /**
         * Get current position with timeout
         * @param {Number} timeout - Timeout in milliseconds
         * @returns {Promise<Object|null>} Position object or null if timeout/error
         */
        async getCurrentPositionWithTimeout(timeout = null) {
            const timeoutMs = timeout || settings.locationSearchTimeout;
            
            try {
                const positionPromise = Geolocation.getCurrentPosition({ 
                    enableHighAccuracy: true,
                    timeout: timeoutMs,
                    maximumAge: 2 * 60 * 1000 // 2 minutes - prevent using cached locations older than 2 minutes
                });
                
                const timeoutPromise = new Promise((_, reject) => {
                    setTimeout(() => reject(new Error('Location timeout')), timeoutMs);
                });
                
                const position = await Promise.race([positionPromise, timeoutPromise]);
                
                if (!position || !position.coords) {
                    console.warn('getCurrentPositionWithTimeout: Invalid position', { position });
                    return null;
                }
                
                return position;
            } catch (error) {
                console.error('getCurrentPositionWithTimeout error:', error);
                return null;
            }
        },
                /**
         * Warm up GPS silently - call this when app becomes active to pre-fetch location.
         * Skips when permission is denied to avoid hanging/unresponsive app (getCurrentPosition
         * can hang on some devices when permission is denied). Single-flight to avoid stacking.
         * @returns {Promise<void>}
         */
        async warmUpGps() {
            if (device === 'web') return;
            if (warmUpInProgress) return;

            try {
                const { location } = await Geolocation.checkPermissions();
                if (location === 'denied') {
                    return;
                }
                if (location !== 'granted') {
                    return; // prompt or unknown - don't trigger a request that could hang
                }
            } catch (e) {
                return; // e.g. plugin not available
            }

            warmUpInProgress = true;
            try {
                await Geolocation.getCurrentPosition({
                    enableHighAccuracy: false,
                    timeout: 5000,
                    maximumAge: 5 * 60 * 1000
                });
                console.log('GPS warm-up completed');
            } catch (error) {
                console.log('GPS warm-up skipped:', error?.message || 'unknown');
            } finally {
                warmUpInProgress = false;
            }
        },
        /**
         * Request location permission with settings guidance if permanently denied
         * @returns {Promise<Object>} { granted: boolean, canRequest: boolean }
         */
        async requestLocationPermission() {
            if (device === 'web') {
                return { granted: false, canRequest: false };
            }

            try {
                // Check current permission status
                const status = await Diagnostic.getLocationAuthorizationStatus();
                const statusUpper = status.toUpperCase();

                // If already granted
                if (['GRANTED', 'GRANTED_WHEN_IN_USE', 'AUTHORIZED_WHEN_IN_USE', 'AUTHORIZED'].includes(statusUpper)) {
                    return { granted: true, canRequest: true };
                }

                // If permanently denied, cannot request again
                if (statusUpper === 'DENIED_ALWAYS') {
                    return { granted: false, canRequest: false };
                }

                // Request permission
                if (['NOT_REQUESTED', 'DENIED_ONCE', 'NOT_DETERMINED'].includes(statusUpper)) {
                    const result = await Diagnostic.requestLocationAuthorization();
                    const resultUpper = result.toUpperCase();
                    
                    if (['GRANTED', 'GRANTED_WHEN_IN_USE', 'AUTHORIZED_WHEN_IN_USE', 'AUTHORIZED'].includes(resultUpper)) {
                        return { granted: true, canRequest: true };
                    }
                    
                    if (resultUpper === 'DENIED_ALWAYS') {
                        return { granted: false, canRequest: false };
                    }
                    
                    return { granted: false, canRequest: true };
                }

                return { granted: false, canRequest: true };
            } catch (error) {
                console.error('requestLocationPermission error:', error);
                return { granted: false, canRequest: false };
            }
        },
        /**
         * Check if position is within location area coordinates
         * @param {Object} position - Position object with coords.latitude and coords.longitude
         * @param {Array} locationArea - Array of {lat, lng} objects
         * @returns {Boolean}
         */
        isPositionInArea(position, locationArea) {
            if (!position || !position.coords || !locationArea || !Array.isArray(locationArea)) {
                return false;
            }

            const { latitude, longitude } = position.coords;
            
            if (typeof latitude !== 'number' || typeof longitude !== 'number') {
                return false;
            }

            try {
                const latLng = new maps.LatLng(latitude, longitude);
                const areaPolygon = new maps.Polygon({
                    paths: locationArea.map(coord => new maps.LatLng(coord.lat, coord.lng))
                });
                console.log('isPositionInArea', latLng, areaPolygon)
                console.log('maps.geometry.poly.containsLocation', maps.geometry.poly.containsLocation(latLng, areaPolygon))
                return maps.geometry.poly.containsLocation(latLng, areaPolygon);
            } catch (error) {
                console.error('isPositionInArea error:', error);
                return false;
            }
        },
    }
}